import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { ApiError } from "@/api/client";
import { utenzeApi } from "@/api/utenze";
import type { Fornitore } from "@/types";

interface Props {
  fornitore: Fornitore;
  username: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// Il backend rifiuta la richiesta con un codice: qui diventa una frase che dice
// la causa e la via d'uscita (regola ux "error-clarity" / "error-recovery").
const MOTIVO_RIFIUTO: Record<string, string> = {
  non_temporanea: "Questa utenza non lavora a sessione temporanea: non serve riaccedere.",
  nessuna_utenza_collegata: "Nessuna utenza collegata a questo fornitore.",
  fuori_finestra_fornitore: "A2A risponde dalle 9 alle 23: riprova in quella fascia.",
  prova_in_corso: "C'è già una verifica in corso su questa utenza. Attendi che finisca.",
};

/**
 * Nuovo accesso al portale del fornitore quando la sessione temporanea è scaduta:
 * password + codice dell'app autenticatore, la verifica gira sul backend.
 * Struttura e classi da ConfirmDialog.tsx (overlay, card bg-card/border-border).
 */
export function RiaccediDialog({ fornitore, username, open, onClose, onSuccess }: Props) {
  const [password, setPassword] = useState("");
  const [codice, setCodice] = useState("");
  const [attesa, setAttesa] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  if (!open) return null;

  const pronto = password.length > 0 && codice.length === 6;

  async function accedi() {
    setErrore(null);
    setAttesa(true);
    try {
      const { job_id } = await utenzeApi.riaccedi(fornitore, { password, codice });
      const esito = await utenzeApi.attendiProva(fornitore, job_id);
      if (esito.stato === "ok") {
        setPassword("");
        setCodice("");
        onSuccess();
        return;
      }
      setErrore(esito.dettaglio);
      setCodice("");
    } catch (e) {
      const codiceErrore = e instanceof ApiError ? e.detail : "";
      setErrore(MOTIVO_RIFIUTO[codiceErrore] ?? "Accesso non riuscito. Riprova tra poco.");
    } finally {
      setAttesa(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={attesa ? undefined : onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="riaccedi-titolo"
        className="bg-card rounded-xl border border-border max-w-sm w-full p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-1">
          <h3 id="riaccedi-titolo" className="font-semibold text-foreground">
            Nuovo accesso {fornitore}
          </h3>
          <p className="text-sm text-muted-foreground">
            Utenza: <span className="font-medium text-foreground break-all">{username}</span>
          </p>
        </div>

        <div className="space-y-1">
          <label htmlFor="riaccedi-password" className="text-sm font-medium text-foreground">
            Password
          </label>
          <input
            id="riaccedi-password"
            type="password"
            autoComplete="current-password"
            value={password}
            disabled={attesa}
            onChange={(e) => setPassword(e.target.value)}
            className="h-9 w-full min-w-0 rounded-lg border border-input bg-transparent px-2.5 text-sm focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50"
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="riaccedi-codice" className="text-sm font-medium text-foreground">
            Codice dell'app autenticatore
          </label>
          <input
            id="riaccedi-codice"
            type="text"
            inputMode="numeric"
            maxLength={6}
            autoComplete="one-time-code"
            value={codice}
            disabled={attesa}
            onChange={(e) => setCodice(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="h-9 w-32 rounded-lg border border-input bg-transparent px-2.5 text-sm text-center tracking-widest font-mono focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50"
          />
        </div>

        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
          <AlertTriangle size={16} aria-hidden="true" className="shrink-0 mt-0.5" />
          <span>Chi è dentro {fornitore} dal browser con questa utenza verrà disconnesso.</span>
        </p>

        {errore && (
          <p role="alert" className="text-sm text-red-600">
            {errore}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={attesa}
            className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-muted transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={accedi}
            disabled={!pronto || attesa}
            className="px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-45 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {attesa && (
              <span
                aria-hidden="true"
                className="h-3.5 w-3.5 rounded-full border-2 border-primary-foreground border-t-transparent animate-spin"
              />
            )}
            {attesa ? "Verifica in corso…" : "Accedi"}
          </button>
        </div>
      </div>
    </div>
  );
}
